import { IMAGE } from '@foyer/shared';

/**
 * Client-side image compression (ADR-0005). A 4 MB phone photo lands near
 * 180 KB, which is roughly what WhatsApp sends.
 *
 * Uses createImageBitmap + canvas rather than WebCodecs — not because
 * WebCodecs is unavailable at the current 3-year device floor (ADR-0002), it
 * likely is available, but nothing here needs the extra control it offers
 * over what canvas.toBlob already does well enough. Worth revisiting only if
 * a real quality/perf gap shows up in testing.
 */

export type Compressed = {
  blob: Blob;
  width: number;
  height: number;
  /** data: URL, ~15 KB. Stored inline so history survives media expiry (ADR-0006). */
  thumb: string;
};

function fit(w: number, h: number, maxEdge: number): [number, number] {
  if (w <= maxEdge && h <= maxEdge) return [w, h];
  const scale = maxEdge / Math.max(w, h);
  return [Math.round(w * scale), Math.round(h * scale)];
}

async function draw(src: ImageBitmap, w: number, h: number): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, w, h);
  return canvas;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((res) => canvas.toBlob(res, type, quality));
}

export async function compressImage(file: File): Promise<Compressed> {
  // 'from-image' pins EXIF-orientation handling explicitly rather than relying
  // on a browser default that has changed across Chrome versions — a sideways
  // photo from an old Samsung is exactly the kind of bug that only shows up on
  // the real device. Re-drawing to canvas then strips all other EXIF (GPS
  // included) as a side effect, which is the intended behaviour, not a gap.
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    const [w, h] = fit(bitmap.width, bitmap.height, IMAGE.maxEdge);
    const canvas = await draw(bitmap, w, h);

    // Older Samsung browsers accept the WebP call but silently emit PNG, which
    // is larger than the original. Check the type we actually got back.
    let blob = await toBlob(canvas, 'image/webp', IMAGE.webpQuality);
    if (!blob || blob.type !== 'image/webp') {
      blob = await toBlob(canvas, 'image/jpeg', IMAGE.jpegQuality);
    }
    if (!blob) throw new Error('image encoding failed');

    const [tw, th] = fit(w, h, IMAGE.thumbEdge);
    const thumbCanvas = await draw(bitmap, tw, th);
    const thumb = thumbCanvas.toDataURL('image/jpeg', IMAGE.thumbQuality);

    return { blob, width: w, height: h, thumb };
  } finally {
    bitmap.close(); // frees native memory immediately — matters on a 2 GB phone
  }
}

export async function sha256Hex(blob: Blob): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const EXT_BY_MIME: Record<string, string> = {
  'image/webp': 'webp',
  'image/jpeg': 'jpg',
  'image/png': 'png',
};

/** "1.2 MB" / "340 KB" — issue #11, so a limited data plan knows the cost before tapping. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Saves an already-fetched blob: URL to the device via a throwaway <a download>. */
export function downloadObjectUrl(objectUrl: string, mediaId: string, mime: string): void {
  const ext = EXT_BY_MIME[mime] ?? 'jpg';
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = `foyer-${mediaId.slice(0, 8)}.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
